import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import CheckCircle from 'lucide-react/dist/esm/icons/check-circle.js';
import { Button } from '@/components/ui/button';
import { useCart } from '@/contexts/CartContext';
import { queueConfirmedPurchase } from '@/lib/purchaseDataLayer';
import { paymentStatusUrl } from '@/lib/paymentRouting';
import {
  GOOGLE_ADS_TRANSACTION_ID_MAX_CHARACTERS,
  GOOGLE_ADS_WEBSITE_PURCHASE_SEND_TO,
} from '../../lib/googleAdsPurchaseContract.js';

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 45;

function isUsableConfirmation(value) {
  const amount = Number(value?.value);
  return Boolean(
    value?.confirmed === true &&
    value?.payment_method === 'card' &&
    String(value?.transaction_id || '').trim() &&
    Number.isFinite(amount) &&
    amount > 0 &&
    value?.currency === 'ILS'
  );
}

export default function ThankYouCC() {
  const { clearCart, resetOrderSession } = useCart();
  const [paymentState, setPaymentState] = useState('verifying');
  const [confirmation, setConfirmation] = useState(null);
  const sentRef = useRef(false);
  const claritySentRef = useRef(false);

  useEffect(() => {
    const statusUrl = paymentStatusUrl(window.location.search);
    if (!statusUrl) {
      setPaymentState('unverified');
      return undefined;
    }

    let cancelled = false;
    let timeoutId = null;
    let attempt = 0;

    const poll = async () => {
      attempt += 1;
      try {
        const response = await fetch(statusUrl, {
          method: 'GET',
          credentials: 'same-origin',
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        });
        const body = await response.json().catch(() => null);
        if (cancelled) return;
        if (response.ok && body?.state === 'payment_verification_conflict') {
          setPaymentState('verification_conflict');
          return;
        }

        if (body?.collected === true && body?.confirmed !== true) {
          setPaymentState(body.state === 'accounting_pending' ? 'accounting_pending' : 'business_pending');
        }
        if (response.ok && isUsableConfirmation(body)) {
          setConfirmation(body);
          setPaymentState('confirmed');
          return;
        }
      } catch (_) {
        // Missing or temporarily unavailable confirmation remains pending data.
      }

      if (cancelled) return;
      if (attempt >= MAX_POLL_ATTEMPTS) {
        setPaymentState((state) => ['accounting_pending', 'business_pending'].includes(state) ? state : 'pending');
        return;
      }
      timeoutId = window.setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();
    return () => {
      cancelled = true;
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    if (
      !isUsableConfirmation(confirmation) ||
      claritySentRef.current ||
      typeof window.clarity !== 'function'
    ) {
      return;
    }
    try {
      window.clarity('set', 'payment_confirmation', 'airtable_business_confirmed');
      window.clarity('event', 'card_purchase_business_confirmed');
      claritySentRef.current = true;
    } catch (_) {
      // Clarity is diagnostic only and never controls payment or conversion state.
    }
  }, [confirmation]);

  useEffect(() => {
    if (!isUsableConfirmation(confirmation) || sentRef.current) return undefined;

    // Queue the GTM purchase independently of the existing direct Ads tag.
    // The verified status is the payment authority; checkout data only enriches it.
    queueConfirmedPurchase({ confirmation });

    const tx = String(confirmation.transaction_id).trim();
    if ([...tx].length > GOOGLE_ADS_TRANSACTION_ID_MAX_CHARACTERS) return undefined;
    const value = Number(confirmation.value);
    const currency = confirmation.currency;
    const guardKey = `gtag_purchase_confirmed_${tx}`;
    let timeoutId = null;
    let attempt = 0;

    const clearCompletedOrder = () => {
      try { localStorage.removeItem('order_payload_for_gtag'); } catch (_) {}
      try { clearCart(); } catch (_) {}
      try { resetOrderSession(); } catch (_) {}
    };

    const fire = () => {
      if (sentRef.current) return;

      try {
        if (localStorage.getItem(guardKey)) {
          sentRef.current = true;
          clearCompletedOrder();
          return;
        }
      } catch (_) {}

      attempt += 1;
      if (typeof window.gtag !== 'function') {
        if (attempt < 40) timeoutId = window.setTimeout(fire, 250);
        return;
      }

      try {
        window.gtag('event', 'conversion', {
          send_to: GOOGLE_ADS_WEBSITE_PURCHASE_SEND_TO,
          value,
          currency,
          transaction_id: tx,
        });
        try { localStorage.setItem(guardKey, '1'); } catch (_) {}
        sentRef.current = true;
        clearCompletedOrder();
      } catch (_) {
        // A failed tag call is not converted into a successful event.
      }
    };

    timeoutId = window.setTimeout(fire, 300);
    return () => {
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [confirmation, clearCart, resetOrderSession]);

  const isConfirmed = paymentState === 'confirmed';
  const isPending = paymentState === 'verifying' || paymentState === 'pending';

  return (
    <>
      <div className="min-h-screen py-12" data-payment-state={paymentState}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
            <CheckCircle className={`h-24 w-24 mx-auto mb-4 ${isConfirmed ? 'text-green-500' : 'text-amber-500'}`} />
            {isConfirmed ? (
              <>
                <h1 className="text-3xl font-bold text-gray-900 mb-4">תשלום התקבל — ההזמנה הועברה לייצור</h1>
                <p className="text-lg text-gray-600 mb-6">תודה! עסקת האשראי אושרה וההזמנה נשלחה לייצור.</p>
              </>
            ) : paymentState === 'business_pending' ? (
              <>
                <h1 className="text-3xl font-bold text-gray-900 mb-4">התשלום התקבל ואנו משלימים את אישור ההזמנה</h1>
                <p className="text-lg text-gray-600 mb-6">התשלום אומת והמסמך הופק. אנו משלימים את אישור ההזמנה. אין לשלם שוב עבור ההזמנה הזאת.</p>
              </>
            ) : paymentState === 'accounting_pending' ? (
              <>
                <h1 className="text-3xl font-bold text-gray-900 mb-4">התשלום התקבל ואנו משלימים את אישור ההזמנה</h1>
                <p className="text-lg text-gray-600 mb-6">התשלום אומת. אנו משלימים את הפקת המסמך ואישור ההזמנה. אין לשלם שוב עבור ההזמנה הזאת.</p>
              </>
            ) : paymentState === 'verification_conflict' ? (
              <>
                <h1 className="text-3xl font-bold text-gray-900 mb-4">נדרשת בדיקה של אישור התשלום</h1>
                <p className="text-lg text-gray-600 mb-6">מצאנו אי התאמה בפרטי האישור. פנו אלינו לבדיקת התשלום. אין לנסות לשלם שוב עבור ההזמנה לפני השלמת הבדיקה.</p>
              </>
            ) : isPending ? (
              <>
                <h1 className="text-3xl font-bold text-gray-900 mb-4">אנו מאמתים את התשלום</h1>
                <p className="text-lg text-gray-600 mb-6">החזרה מעמוד האשראי התקבלה. ההזמנה לא תסומן כמשולמת ולא תדווח כרכישה עד שנקבל אישור שרת מאומת.</p>
              </>
            ) : (
              <>
                <h1 className="text-3xl font-bold text-gray-900 mb-4">לא ניתן לאמת את התשלום מהקישור הזה</h1>
                <p className="text-lg text-gray-600 mb-6">לא די בהגעה לעמוד הזה כדי לאשר עסקה. אם חויבת, האישור יטופל אוטומטית דרך ספק האשראי.</p>
              </>
            )}

            <div className="flex items-center justify-center gap-3 mb-6">
              <Link to="/catalog"><Button>המשך לקניות</Button></Link>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
}
